import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import ComposizioneMail from './ComposizioneMail';

function ContattiPersonalizzati() {
  const [mode, setMode] = useState('excel'); // 'excel' | 'manual'
  const [rows, setRows] = useState([]); // [{ Nome, Email ORD, Email PEC, ...segnaposti }]
  const [selected, setSelected] = useState({}); // key: true/false
  const [placeholders, setPlaceholders] = useState([]); // nomi colonne extra o manuali
  const [manualEmails, setManualEmails] = useState('');
  const [manualFields, setManualFields] = useState([]); // [{ name, value }]
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Excel template download
  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['Nome', 'Email ORD', 'Email PEC', 'Segnaposto1', 'Segnaposto2'],
      ['Mario Rossi', 'mario@rossi.it', 'mario@pec.it', 'Valore1', 'Valore2'],
      ['Anna Bianchi', 'anna@bianchi.it', 'anna@pec.it', 'ValoreA', 'ValoreB']
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Destinatari');
    XLSX.writeFile(wb, 'template_personalizzato.xlsx');
  };

  // Excel upload & parsing
  const handleFileUpload = async (e) => {
    setError('');
    setLoading(true);
    const file = e.target.files[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      // Validazione colonne
      const required = ['Nome', 'Email ORD', 'Email PEC'];
      const columns = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0] || [];
      if (!required.every(col => columns.includes(col))) {
        setError('Il file deve contenere le colonne obbligatorie: Nome, Email ORD, Email PEC');
        setLoading(false);
        return;
      }
      // Validazione email
      const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      const validRows = json.filter(r => r['Nome'] && (isValidEmail(r['Email ORD']) || isValidEmail(r['Email PEC'])));
      // Segnaposto: tutte le colonne extra, formato {NOME}
      const extraCols = columns.filter(col => !required.includes(col)).map(col => `{${col}}`);
      setPlaceholders(extraCols);
      setRows(validRows.map((r, idx) => ({
        ...r,
        emailOrd: r['Email ORD'] ? [r['Email ORD']] : [],
        emailPec: r['Email PEC'] ? [r['Email PEC']] : [],
        key: `${r['Nome']}_${r['Email ORD']}_${r['Email PEC']}_${idx}`
      })));
      // Seleziona tutti di default
      const sel = {};
      validRows.forEach((r, idx) => { sel[`${r['Nome']}_${r['Email ORD']}_${r['Email PEC']}_${idx}`] = true; });
      setSelected(sel);
    } catch (err) {
      setError('Errore nella lettura del file.');
    }
    setLoading(false);
  };

  // Manual email parsing
  const handleManualParse = () => {
    setError('');
    const emails = manualEmails.split(/[,;\n]+/).map(e => e.trim()).filter(e => e);
    const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    // Crea un oggetto con tutti i segnaposto manuali
    const segnapostoObj = {};
    manualFields.forEach(f => { segnapostoObj[f.name] = f.value; });
    const validRows = emails.filter(isValidEmail).map((email, idx) => ({
      ...segnapostoObj,
      emailOrd: [email],
      emailPec: [],
      key: `${email}_${idx}`
    }));
    setRows(validRows);
    const sel = {};
    validRows.forEach((r, idx) => { sel[r.key] = true; });
    setSelected(sel);
    setPlaceholders(manualFields.map(f => `{${f.name}}`));
  };

  // Add manual field
  const handleAddField = () => {
    if (!newFieldName) return;
    setManualFields([...manualFields, { name: newFieldName, value: newFieldValue }]);
    setNewFieldName('');
    setNewFieldValue('');
    setPlaceholders([...placeholders, `{${newFieldName}}`]);
  };

  // Table rendering
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const selectedEmails = rows.filter(r => selected[r.key]).length;
  const totalItems = rows.length;
  const totalEmails = rows.reduce((sum, r) => {
    let count = 0;
    if (r['Email ORD']) count++;
    if (r['Email PEC']) count++;
    return sum + count;
  }, 0);

  // Dati da passare a ComposizioneMail
  const selectedRows = rows.filter(r => selected[r.key]).map(r => {
    // Unisci i segnaposto manuali ai dati
    let extra = {};
    if (mode === 'manual') {
      manualFields.forEach(f => { extra[f.name] = f.value; });
    }
    // Per compatibilità con ComposizioneMail, copia anche 'Nome' come 'nome' se presente
    let nome = r.Nome || '';
    return {
      ...r,
      ...(nome ? { nome } : {}),
      ...extra
    };
  });

  return (
    <>
      <div className="card mb-4">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0 text-primary"><i className="fas fa-user-edit me-2"></i> 1. Destinatari Personalizzati</h5>
            <div className="d-flex align-items-center">
              <span className="badge bg-primary me-2">
                <i className="fas fa-list-ol me-1"></i> Elementi {totalItems}
              </span>
              <span className="badge bg-info">
                <i className="fas fa-at me-1"></i> Email totali {totalEmails}
              </span>
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <div className="nav nav-tabs mb-2" role="tablist">
              <button className={`nav-link ${mode === 'excel' ? 'active' : ''}`} onClick={() => setMode('excel')} type="button">
                <i className="fas fa-file-excel me-1"></i> Importa Contatti da Excel
              </button>
              <button className={`nav-link ${mode === 'manual' ? 'active' : ''}`} onClick={() => setMode('manual')} type="button">
                <i className="fas fa-keyboard me-1"></i> Importa Contatti Manuale
              </button>
            </div>
          </div>
          {mode === 'excel' ? (
            <>
              <div className="mb-3">
                <input type="file" accept=".xlsx" className="form-control" onChange={handleFileUpload} disabled={loading} />
                <button className="btn btn-link" type="button" onClick={handleDownloadTemplate}><i className="fas fa-download"></i> Scarica template Excel</button>
              </div>
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="table-responsive" style={{ maxHeight: 300, overflowY: 'auto' }}>
                <table className="table table-striped table-hover mb-0">
                  <thead className="table-dark sticky-top">
                    <tr>
                      <th><input type="checkbox" checked={selectedCount === rows.length && rows.length > 0} ref={el => { if (el) el.indeterminate = selectedCount > 0 && selectedCount < rows.length; }} onChange={e => setSelected(rows.reduce((acc, r) => { acc[r.key] = e.target.checked; return acc; }, {}))} /></th>
                      <th>Nome</th>
                      <th>Email ORD</th>
                      <th>Email PEC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length > 0 ? rows.map(r => (
                      <tr key={r.key}>
                        <td><input type="checkbox" checked={!!selected[r.key]} onChange={e => setSelected({ ...selected, [r.key]: e.target.checked })} /></td>
                        <td>{r['Nome']}</td>
                        <td>{r['Email ORD']}</td>
                        <td>{r['Email PEC']}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={4} className="text-center text-muted py-4">Nessun destinatario importato</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="mb-3">
                <label className="form-label fw-bold">Incolla indirizzi email</label>
                <textarea className="form-control" rows={3} value={manualEmails} onChange={e => setManualEmails(e.target.value)} placeholder="email1@dominio.it, email2@dominio.it..." />
                <button className="btn btn-primary mt-2" type="button" onClick={handleManualParse}><i className="fas fa-list me-1"></i> Crea elenco</button>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold">Aggiungi segnaposto</label>
                <div className="input-group">
                  <input type="text" className="form-control" placeholder="Nome segnaposto" value={newFieldName} onChange={e => setNewFieldName(e.target.value)} />
                  <input type="text" className="form-control" placeholder="Valore" value={newFieldValue} onChange={e => setNewFieldValue(e.target.value)} />
                  <button className="btn btn-success" type="button" onClick={handleAddField}><i className="fas fa-plus"></i></button>
                </div>
                {manualFields.length > 0 && (
                  <ul className="mt-2">
                    {manualFields.map((f, idx) => <li key={idx}><code>{`{${f.name}}`}</code>: {f.value}</li>)}
                  </ul>
                )}
              </div>
              <div className="table-responsive" style={{ maxHeight: 300, overflowY: 'auto' }}>
                <table className="table table-striped table-hover mb-0">
                  <thead className="table-dark sticky-top">
                    <tr>
                      <th><input type="checkbox" checked={selectedCount === rows.length && rows.length > 0} ref={el => { if (el) el.indeterminate = selectedCount > 0 && selectedCount < rows.length; }} onChange={e => setSelected(rows.reduce((acc, r) => { acc[r.key] = e.target.checked; return acc; }, {}))} /></th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length > 0 ? rows.map(r => (
                      <tr key={r.key}>
                        <td><input type="checkbox" checked={!!selected[r.key]} onChange={e => setSelected({ ...selected, [r.key]: e.target.checked })} /></td>
                        <td>{r.emailOrd && r.emailOrd.length > 0 ? r.emailOrd[0] : '-'}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={2} className="text-center text-muted py-4">Nessun destinatario inserito</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Spazio tra le card */}
      <div style={{height: '1.5rem'}}></div>
      {/* 🔧 COMPOSIZIONE MAIL SEMPRE VISIBILE */}
      <div className="mb-4">
        <ComposizioneMail
          destinatariCount={selectedCount}
          emailCount={selectedEmails}
          servizio="Personalizzato"
          placeholders={placeholders}
          selectedRows={selectedRows}
          onSendComplete={() => setSelected({})}
        />
      </div>
    </>
  );
}

export default ContattiPersonalizzati;
